import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  type SvgIconProps,
} from '@mui/material';
import { InfoTip } from './InfoTip';

/** Theme palette colours a card or breakdown row may use. */
export type MetricColor = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';

type MetricIcon = React.ReactElement<SvgIconProps>;

/** Clones an icon with extra styles, keeping any sx the caller already set. */
function styledIcon(icon: MetricIcon, sx: SvgIconProps['sx']): MetricIcon {
  const own = icon.props.sx;
  return React.cloneElement(icon, { sx: [sx, ...(Array.isArray(own) ? own : [own])] });
}

/**
 * One named part of a card's headline value. The label sits in an icon plus a
 * tooltip, because three labelled entries do not fit on one line.
 */
export interface MetricBreakdownRow {
  /** Short name, used for the accessible label. */
  label: string;
  /** Sentence shown in the tooltip. */
  description: string;
  value: number;
  color: MetricColor;
  icon: MetricIcon;
}

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: MetricIcon;
  color: MetricColor;
  subtext?: string;
  loading?: boolean;
  /** Definition shown on an info icon beside the title, for text too long for the subtext. */
  info?: string;
  /**
   * Optional split of the headline value. Percentages are against the sum of the
   * rows, so they describe what is on screen; rounding can make them read 99% or 101%.
   */
  breakdown?: MetricBreakdownRow[];
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color,
  subtext,
  loading,
  info,
  breakdown,
}) => {
  const theme = useTheme();
  const main = theme.palette[color].main;
  const breakdownTotal = breakdown?.reduce((sum, row) => sum + row.value, 0) ?? 0;
  const pctOf = (n: number) => (breakdownTotal > 0 ? Math.round((n / breakdownTotal) * 100) : 0);

  return (
    <Card variant="outlined" elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {title}
              {info && <InfoTip label={title} text={info} />}
            </Typography>
            {loading ? (
              <CircularProgress size={32} sx={{ my: 1 }} />
            ) : (
              <Typography variant="h3" fontWeight={700} sx={{ color: main, mb: 0.5 }}>
                {value}
              </Typography>
            )}
            {subtext && (
              <Typography variant="caption" color="text.secondary">
                {subtext}
              </Typography>
            )}
            {/* Takes the subtext's place. Each entry is one nowrap text node, so a
                count and its percentage never split across lines. */}
            {!loading && breakdown && breakdown.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 0.25, columnGap: 1.25 }}>
                {breakdown.map((row) => (
                  <Tooltip key={row.label} title={`${row.label}: ${row.description}`}>
                    {/* The label lives only in the icon, so role makes the aria-label
                        count and tabIndex lets keyboard and touch users open the tooltip.
                        role="img" hides the percentage, so it is repeated in the label. */}
                    <Box
                      role="img"
                      tabIndex={0}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                      aria-label={`${row.label}: ${row.value.toLocaleString()}, ${pctOf(row.value)}% of messages sent`}
                    >
                      {styledIcon(row.icon, { fontSize: 15, color: `${row.color}.main`, flexShrink: 0 })}
                      <Typography variant="caption" color="text.secondary" noWrap>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {row.value.toLocaleString()}
                        </Box>{' '}
                        ({pctOf(row.value)}%)
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(main, 0.15)}, ${alpha(main, 0.03)})`,
              borderRadius: '50%',
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: `2px solid ${alpha(main, 0.19)}`,
            }}
          >
            {styledIcon(icon, { fontSize: 26, color: main })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
